import { useState, type FormEvent } from 'react';
import { api, errorMessage } from '../../lib/api';
import { generatePassword, timeAgo } from '../../lib/format';
import { useLoad } from '../../lib/hooks';
import type { Member, MemberRole, Project } from '../../lib/types';
import { useAuth } from '../../state/auth';
import { Icon } from '../../components/Icon';
import { ConfirmDialog, CopyButton, Dialog, Field, Menu, Spinner, Switch } from '../../components/ui';
import { useToast } from '../../components/toast';

const ROLE_LABEL: Record<MemberRole, string> = { manager: 'Manager', commenter: 'Can add revisions', viewer: 'View only' };

function GateCard({ project, onProjectChange }: { project: Project; onProjectChange: (project: Project) => void }) {
  const toast = useToast();
  const gate = project.gate!;
  const [username, setUsername] = useState(gate.username || project.slug);
  const [password, setPassword] = useState('');
  // The password that was just saved. It exists only in this tab: the server keeps a hash.
  const [saved, setSaved] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (body: Record<string, unknown>, message: string) => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.put<{ project: Project }>(`/api/projects/${project.id}/gate`, body);
      onProjectChange(result.project);
      toast.show(message);
      return true;
    } catch (err) {
      setError(errorMessage(err));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const body: Record<string, unknown> = { username, enabled: true };
    if (password) body.password = password;
    if (await save(body, password ? 'Project sign-in saved. Anyone using the old password has been signed out.' : 'Project sign-in saved.')) {
      if (password) setSaved(password);
      setPassword('');
    }
  };

  // Built from what the server has, never from unsaved edits.
  const handover = saved ? `Review link: ${project.shareUrl}\nProject username: ${gate.username || project.slug}\nProject password: ${saved}` : null;

  return (
    <div className="card card-pad stack" style={{ gap: 16 }}>
      <div className="row-between">
        <div>
          <h3 className="card-title">Project sign-in</h3>
          <p className="muted" style={{ maxWidth: '60ch' }}>
            One username and password for this project. Each reviewer also enters their own name and email, so every visit and revision is attributed to a person.
          </p>
        </div>
        <div className="row">
          <span className={`badge ${gate.enabled ? 'badge-open' : ''}`}>{gate.enabled ? 'On' : 'Off'}</span>
          <Switch
            label="Project sign-in"
            checked={gate.enabled}
            disabled={busy || (!gate.hasPassword && !gate.enabled)}
            onChange={(next) => void save({ enabled: next }, next ? 'Project sign-in is on.' : 'Project sign-in is off. Guests can no longer enter.')}
          />
        </div>
      </div>

      <form className="stack" style={{ gap: 16 }} onSubmit={submit}>
        <dl className="kv">
          <dt>Review link</dt>
          <dd className="row">
            <code className="truncate">{project.shareUrl}</code>
            <CopyButton text={project.shareUrl} />
          </dd>
          <dt>
            <label htmlFor="gate-username">Project username</label>
          </dt>
          <dd>
            <input id="gate-username" className="input" style={{ maxWidth: 320 }} value={username} onChange={(event) => setUsername(event.target.value)} required autoCapitalize="none" />
          </dd>
          <dt>
            <label htmlFor="gate-password">{gate.hasPassword ? 'New password' : 'Password'}</label>
          </dt>
          <dd>
            <div className="input-row" style={{ maxWidth: 420 }}>
              <input
                id="gate-password"
                className="input mono"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder={gate.hasPassword ? 'Leave blank to keep the current one' : 'At least 8 characters'}
                minLength={8}
                required={!gate.hasPassword}
                autoComplete="off"
              />
              <button type="button" className="btn btn-secondary" onClick={() => setPassword(generatePassword())}>
                Generate
              </button>
            </div>
            <span className="field-hint">{gate.hasPassword ? 'Passwords are stored hashed and cannot be shown again. Setting a new one signs current guests out.' : 'Set a password to turn project sign-in on.'}</span>
          </dd>
        </dl>
        {error && (
          <div className="notice notice-error" role="alert">
            {error}
          </div>
        )}
        <div className="row">
          <button className="btn btn-primary" disabled={busy}>
            {busy && <span className="spinner" />}
            {gate.enabled ? 'Save' : 'Save and turn on'}
          </button>
          {handover && !password && <CopyButton text={handover} label="Copy link, username and password" className="btn btn-secondary" />}
        </div>
        {handover && !password && (
          <span className="field-hint" role="status">
            Copy the details now. The password cannot be shown again once you leave this page.
          </span>
        )}
      </form>
    </div>
  );
}

function AddMemberDialog({ project, onClose, onAdded }: { project: Project; onClose: () => void; onAdded: () => void }) {
  const { meta, isStaff } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [role, setRole] = useState<MemberRole>('commenter');
  // Sign-in details are handed out by MK-Way staff only.
  const [withPassword, setWithPassword] = useState(isStaff && !meta?.emailEnabled);
  const [password, setPassword] = useState(() => generatePassword());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ inviteLink: string | null; emailSent: boolean; password: string | null; created: boolean; username: string | null } | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const response = await api.post<{ member: Member; inviteLink: string | null; emailSent: boolean; passwordSet: boolean; created: boolean }>(`/api/projects/${project.id}/members`, {
        name,
        email,
        role,
        ...(withPassword ? { password } : {}),
        ...(withPassword && username.trim() ? { username: username.trim() } : {}),
      });
      onAdded();
      // The typed password only counts if the server actually set it (a new account, or one that had none).
      setResult({ inviteLink: response.inviteLink, emailSent: response.emailSent, password: response.passwordSet ? password : null, created: response.created, username: response.member?.username ?? null });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    const details = result.password
      ? `Sign in: ${meta?.appUrl ?? location.origin}/login\nEmail: ${email}${result.username ? `\nUsername: ${result.username}` : ''}\nPassword: ${result.password}`
      : `Set your password: ${result.inviteLink}`;
    return (
      <Dialog
        title={`${name} has been added`}
        onClose={onClose}
        footer={
          <button className="btn btn-primary" onClick={onClose}>
            Done
          </button>
        }
      >
        {!result.password && !result.inviteLink ? (
          result.emailSent ? (
            <p className="muted">An invitation email is on its way to {email}, with a link to choose a password.</p>
          ) : result.created ? (
            <p className="muted">They are on the project. This server cannot send email, so ask MK-Way to send them a sign-in link, or share the project password with them.</p>
          ) : (
            <p className="muted">They already have a Revisions account, so they can open this project the way they normally sign in. Nothing else to send.</p>
          )
        ) : result.password ? (
          <p className="muted">Send them these sign-in details. The password is not shown again.</p>
        ) : result.emailSent ? (
          <p className="muted">An invitation email is on its way to {email}. You can also send them this link yourself.</p>
        ) : (
          <p className="muted">Email is not set up on this server, so send them this one-time link to choose a password. It works for 7 days.</p>
        )}
        {(result.password || result.inviteLink) && (
          <>
            <pre className="codeblock" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
              {details}
            </pre>
            <CopyButton text={details} label="Copy details" />
          </>
        )}
      </Dialog>
    );
  }

  return (
    <Dialog title="Add a person" description="They get their own sign-in and see only the projects they are added to." onClose={onClose}>
      <form className="stack" style={{ gap: 16 }} onSubmit={submit}>
        <Field label="Name">{(id) => <input id={id} className="input" value={name} onChange={(event) => setName(event.target.value)} required />}</Field>
        <Field label="Email">{(id) => <input id={id} className="input" type="email" value={email} onChange={(event) => setEmail(event.target.value)} required />}</Field>
        <Field label="Access">
          {(id) => (
            <select id={id} className="select" value={role} onChange={(event) => setRole(event.target.value as MemberRole)}>
              <option value="commenter">Can add revisions, approve and upload files</option>
              <option value="viewer">View only</option>
              <option value="manager">Manager: can also add items and people</option>
            </select>
          )}
        </Field>
        {isStaff && (
          <label className="check">
            <input type="checkbox" checked={withPassword} onChange={(event) => setWithPassword(event.target.checked)} />
            Set a password for them now
          </label>
        )}
        {withPassword && (
          <Field label="Username" hint="Optional. They can sign in with it instead of their email.">
            {(id) => <input id={id} className="input" value={username} onChange={(event) => setUsername(event.target.value)} maxLength={40} autoCapitalize="none" spellCheck={false} />}
          </Field>
        )}
        {withPassword ? (
          <Field label="Password" hint="Only used for a new account. Someone who already signs in keeps their password.">
            {(id) => (
              <div className="input-row">
                <input id={id} className="input mono" value={password} onChange={(event) => setPassword(event.target.value)} minLength={8} required autoComplete="off" />
                <button type="button" className="btn btn-secondary" onClick={() => setPassword(generatePassword())}>
                  Generate
                </button>
              </div>
            )}
          </Field>
        ) : (
          <p className="field-hint">
            {meta?.emailEnabled
              ? 'They will get an email with a link to choose their own password.'
              : isStaff
                ? 'You will get a one-time link to send them, so they can choose their own password.'
                : 'They can come in with the project password, or MK-Way can send them their own sign-in link.'}
          </p>
        )}
        {error && (
          <div className="notice notice-error" role="alert">
            {error}
          </div>
        )}
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" disabled={busy}>
            {busy && <span className="spinner" />}
            Add to project
          </button>
        </div>
      </form>
    </Dialog>
  );
}

export function AccessTab({ project, onProjectChange }: { project: Project; onProjectChange: (project: Project) => void }) {
  const toast = useToast();
  const { isStaff } = useAuth();
  const { data, loading, error, reload } = useLoad(() => api.get<{ members: Member[] }>(`/api/projects/${project.id}/members`), [project.id]);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<Member | null>(null);
  const [link, setLink] = useState<{ member: Member; url: string } | null>(null);
  const members = data?.members ?? [];

  const changeRole = async (member: Member, role: MemberRole) => {
    try {
      await api.patch(`/api/projects/${project.id}/members/${member.id}`, { role });
      await reload(true);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const passwordLink = async (member: Member) => {
    try {
      const result = await api.post<{ inviteLink: string }>(`/api/projects/${project.id}/members/${member.id}/invite-link`, {});
      setLink({ member, url: result.inviteLink });
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <div className="stack" style={{ gap: 24 }}>
      {project.gate && <GateCard project={project} onProjectChange={onProjectChange} />}

      <div className="stack" style={{ gap: 12 }}>
        <div className="section-head">
          <div>
            <h3 className="card-title">People</h3>
            <p className="muted">MK-Way team members can open every project. Clients see only the projects they are added to.</p>
          </div>
          <button className="btn btn-secondary" onClick={() => setAdding(true)}>
            <Icon name="plus" /> Add a person
          </button>
        </div>

        {loading && !data ? (
          <Spinner label="Loading people" />
        ) : error && !data ? (
          <div className="notice notice-error row-between" role="alert">
            <span>The people on this project could not be loaded. {error}</span>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => void reload()}>
              Try again
            </button>
          </div>
        ) : members.length === 0 ? (
          <div className="notice">No client accounts on this project yet. Add a person, or turn on project sign-in above and share the link.</div>
        ) : (
          <div className="card table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Person</th>
                  <th>Access</th>
                  <th>Signs in with</th>
                  <th>Last seen</th>
                  <th>
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {members.map((member) => (
                  <tr key={member.id}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{member.name}</div>
                      <div className="muted" style={{ fontSize: 'var(--fs-xs)' }}>
                        {member.email}
                      </div>
                    </td>
                    <td>
                      <select className="select select-inline" aria-label={`Access for ${member.name}`} value={member.memberRole} onChange={(event) => void changeRole(member, event.target.value as MemberRole)} style={{ border: '1px solid var(--border-medium)' }}>
                        {(Object.keys(ROLE_LABEL) as MemberRole[]).map((role) => (
                          <option key={role} value={role}>
                            {ROLE_LABEL[role]}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="muted">{member.hasPassword ? 'Own password' : member.addedVia === 'gate' ? 'Project password' : 'Invite pending'}</td>
                    <td className="muted" style={{ whiteSpace: 'nowrap' }}>
                      {member.lastLoginAt ? timeAgo(member.lastLoginAt) : 'Never'}
                    </td>
                    <td>
                      <div className="row" style={{ justifyContent: 'flex-end' }}>
                        <Menu
                          label={`Actions for ${member.name}`}
                          trigger={<Icon name="more" />}
                          items={[
                            // Sign-in details of existing accounts are handled by MK-Way staff only.
                            ...(isStaff ? [{ label: member.hasPassword ? 'Password reset link' : 'Set-password link', icon: 'key' as const, onSelect: () => void passwordLink(member) }] : []),
                            { label: 'Remove from project', icon: 'trash' as const, danger: true, onSelect: () => setRemoving(member) },
                          ]}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {adding && <AddMemberDialog project={project} onClose={() => setAdding(false)} onAdded={() => void reload(true)} />}
      {link && (
        <Dialog
          title={`Password link for ${link.member.name}`}
          description="Send them this one-time link. It works for 7 days."
          onClose={() => setLink(null)}
          footer={
            <button className="btn btn-primary" onClick={() => setLink(null)}>
              Done
            </button>
          }
        >
          <pre className="codeblock" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
            {link.url}
          </pre>
          <CopyButton text={link.url} label="Copy link" />
        </Dialog>
      )}
      {removing && (
        <ConfirmDialog
          title={`Remove ${removing.name}?`}
          message="They lose access to this project immediately. Their revisions stay."
          confirmLabel="Remove"
          danger
          onClose={() => setRemoving(null)}
          onConfirm={async () => {
            try {
              await api.del(`/api/projects/${project.id}/members/${removing.id}`);
              await reload(true);
            } catch (err) {
              toast.error(errorMessage(err));
            }
          }}
        />
      )}
    </div>
  );
}
